import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import { getSelectedStoreTenant, getTenants } from '../selectors/tenant.selectors';


import * as ClosuresActions from '../actions/closures.actions';
import { NewClosuresComponent } from '../../modules/home/modules/closures/components/new-closures/new-closures.component';
import { getSelectionDialogId, getClosureDialogId, getClosuresTableState} from '../selectors/closures.selectors';
import { LaravelClosuresService } from '../../commons/services/laravel/laravel-closures.service';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class ClosureEffects {
    error$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(
                ClosuresActions.deleteClosureFailed,
                ClosuresActions.saveClosureFailed
            ),
            tap(({ error }) => {
              if (error) {
                this.alertService.showErrorMessage('Errore', error);
              }
            })
          ),
        { dispatch: false }
    );

    loadClosures$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.loadClosures),
          withLatestFrom(this.store$.select(getSelectedStoreTenant)),
          map(([{ page, perPage, order, direction, filters, includes }, tenant]) => {
            const filter = {...filters};
            if(filters.onlyCurrentTenant){
              filter.tenant_id = tenant.id
            }
            return {page, perPage, order, direction, filters: filter, includes};
          }),
          switchMap(({page, perPage, order, direction, filters, includes}) => {
            return this.closureService
              .list(page, perPage, order, direction, filters, includes)
              .pipe(
                map((result) => {
                  return ClosuresActions.loadClosuresCompleted({
                      closures: result.data,
                      currentPage: page,
                      total: result.total,
                      perPage,
                      order,
                      direction,
                      filters,
                      includes
                    });
                }),

                catchError((error) =>
                  of(ClosuresActions.loadClosuresFailed({ error })))
              );
            })
          )
      );

    changePage = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.changePage),
          withLatestFrom(this.store$.select(getClosuresTableState)),
          map(
            ([
              { page, size },
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
              ClosuresActions.loadClosures({
                page: page,
                perPage: size,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    changeSort = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.changeSort),
          withLatestFrom(this.store$.select(getClosuresTableState)),
          map(
            ([
              action,
              { total, currentPage, perPage, direction, order, filters, includes },
            ]) =>
              ClosuresActions.loadClosures({
                page: currentPage,
                perPage: perPage,
                order: action.order,
                direction: action.direction,
                filters,
                includes,
              })
          )
        )
    );

    changeFilters = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.changeFilters),
          withLatestFrom(this.store$.select(getClosuresTableState)),
          map(
            ([
              { filters },
              { total, currentPage, perPage, direction, order, includes },
            ]) =>
                ClosuresActions.loadClosures({
                page: currentPage,
                perPage: perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    // editClosure$ = createEffect(() =>
    //     this.actions$.pipe(
    //       ofType(ClosuresActions.editClosure),
    //       withLatestFrom(this.store$.select(getTenants)),
    //       map(([action, tenants]) => {
    //         let dialogRef = this.dialog.open(NewClosuresComponent, {
    //           data: { closure: action.closure, tenants },
    //           panelClass: 'full-screen-dialog'
    //         });
    //         return ClosuresActions.closureDialogOpened({ dialogId: dialogRef.id });
    //       })
    //     )
    //   );

    editClosure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ClosuresActions.editClosure),
        map(({ closure }) => {
          let dialogRef = this.dialog.open(NewClosuresComponent, {
            data: {
              closure,
            },
            panelClass: 'full-screen-dialog',
          });
          return ClosuresActions.closureDialogOpened({
            dialogId: dialogRef.id,
          });
        })
      )
    );


    saveClosure$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.saveClosure),
          withLatestFrom(this.store$.select(getSelectedStoreTenant)),
          mergeMap(([{ closures }, selectedTenant]) => {
            let tenantId = selectedTenant ? selectedTenant.id : null;
            let closureSave = {
              ...closures.toDTO(),
              tenant_id: tenantId
            };
            return this.closureService.upsert(closureSave).pipe(
                map((result) =>
                  ClosuresActions.saveClosureCompleted({ closure: result })
                ),
              //   catchError((error) =>
              //   of(ClosuresActions.saveClosureFailed({ error }))
              // )
              tap((action) => {
                if (action.type === ClosuresActions.saveClosureCompleted.type) {
                  this.alertService.showConfirmMessage(`Data salvata con successo`)
                  this.store$.dispatch(ClosuresActions.closeClosuresDialog());
                } else {
                  this.store$.dispatch(ClosuresActions.saveClosureFailed({ error: Error }));
                }
              })
            );
          })
        )
    );

    // onSaveCompleted$ = createEffect(() =>
    //     this.actions$.pipe(
    //       ofType(ClosuresActions.saveClosureCompleted),
    //       map((action) => action.closure),
    //       tap((closure) =>
    //         this.alertService.showConfirmMessage(
    //           `Data con id ${closure.id} salvata con successo`
    //         )
    //       ),
    //       map(() => ClosuresActions.closeClosuresDialog())
    //     )
    // );


    deleteClosure$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.deleteClosure),
          switchMap(({ closure }) =>
            this.alertService
              .showConfirmDialog(
                'Conferma eliminazione',
                `Sei sicuro di voler eliminare l'elemento selezionato?`
              )
              .pipe(
                mergeMap((confirm) => {
                  return confirm
                    ? this.closureService.delete(closure.id).pipe(
                        map(() =>
                          ClosuresActions.deleteClosureCompleted({
                            closure,
                          })
                        ),
                        catchError((error) =>
                          of(ClosuresActions.deleteClosureFailed({ error }))
                        ),
                        tap((action) => {
                          if (action.type === ClosuresActions.deleteClosureCompleted.type) {
                            this.alertService.showConfirmMessage(` Data eliminata con successo`);
                            this.store$.dispatch(ClosuresActions.closeClosuresDialog());
                          }
                        })
                      )
                    : of(ClosuresActions.deleteClosureCancelled());
                })
              )
          )
        )
    );

    // onDeleteCompleted$ = createEffect(() =>
    //     this.actions$.pipe(
    //       ofType(ClosuresActions.deleteClosureCompleted),
    //       tap(({ closure }) =>
    //         this.alertService.showConfirmMessage(
    //           ` Data con id ${closure.id} eliminata con successo`
    //         )
    //       ),
    //       map(() => ClosuresActions.closeClosuresDialog())
    //     )
    // );

    // closeDialog = createEffect(
    //     () =>
    //       this.actions$.pipe(
    //         ofType(ClosuresActions.closeClosuresDialog),
    //         withLatestFrom(this.store$.select(getClosureDialogId)),
    //         tap(([_, dialogId]) => {
    //           if (dialogId) {
    //             this.dialog.getDialogById(dialogId).close();
    //           }
    //         })
    //       ),
    //     { dispatch: false }
    // );

    reloadAfterSave = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.saveClosureCompleted),
          withLatestFrom(this.store$.select(getClosuresTableState)),
          map(
            ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
              ClosuresActions.loadClosures({
                page: currentPage,
                perPage,
                order,
                direction,
                filters,
                includes,
              })
          )
        )
    );

    reloadAfterDelete = createEffect(() =>
    this.actions$.pipe(
        ofType(ClosuresActions.deleteClosureCompleted),
        withLatestFrom(this.store$.select(getClosuresTableState)),
        map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
            ClosuresActions.loadClosures({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
            })
        )
    )
    );


    closeSelectionDialog = createEffect(
    () =>
        this.actions$.pipe(
        ofType(ClosuresActions.closeSelectionDialog),
        withLatestFrom(this.store$.select(getSelectionDialogId)),
        tap(([_, dialogId]) => {
            if (dialogId) {
            this.dialog.getDialogById(dialogId).close();
            }
        })
        ),
    { dispatch: false }
    );

    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.requestCloseClosuresDialog),
          switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
              disableClose: false
            });

            // Imposta il messaggio del dialog
            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
              "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";

            return confirmDialogRef.afterClosed().pipe(
              map(result =>
                result
                  ? ClosuresActions.confirmCloseClosuresDialog()
                  : ClosuresActions.cancelCloseClosuresDialog()
              )
            );
          })
        )
      );


      confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.confirmCloseClosuresDialog),
          map(() => ClosuresActions.closeClosuresDialog())
        )
      );

      cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(ClosuresActions.cancelCloseClosuresDialog),
          map(() => {
            // Se l'utente annulla mantiene il dialog aperto
            return { type: 'NO_ACTION' };
          })
        )
      );

      closeDialog$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(ClosuresActions.closeClosuresDialog),
            withLatestFrom(this.store$.select(getClosureDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
      );



    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private closureService: LaravelClosuresService,
        private dialog: MatDialog,
        private alertService: AlertService
    ) {}
}
